import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseUrl } from '../utils/config';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  TextField,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';

const FIXED_HEADER_HEIGHT_DESKTOP = '64px';
const FIXED_HEADER_HEIGHT_MOBILE = '56px';

// Unique marker for assignee-submitted values (only for internal processing and backend)
const ASSIGNEE_MARKER = '[ASSIGNEE]';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_DESKTOP})`,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(8),
  boxSizing: 'border-box',
  overflowY: 'auto',
  overflowX: 'hidden',

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_MOBILE})`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '2000px',
  borderRadius: '8px',
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[3],

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const TableWrapper = styled('div')(({ theme }) => ({
  overflowX: 'auto',
  width: '100%',
  marginBottom: theme.spacing(3),

  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '700px',

    '& th, & td': {
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(1),
      textAlign: 'left',
      verticalAlign: 'top',
      whiteSpace: 'nowrap',
      fontSize: '0.85rem',

      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.75),
        fontSize: '0.75rem',
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.5),
        fontSize: '0.7rem',
      },
    },

    // Default header styles if fillColor is not present or specific
    // These will be overridden by inline style if cell.fillColor is defined
    '& th': {
        // backgroundColor: theme.palette.primary.light, // This can now be primarily controlled by fillColor
        color: theme.palette.primary.contrastText, // Keep text color if not overridden by fontColor
        fontWeight: 'bold',
    },

    // Style for the input within cells that were originally marked by the assignee
    '& .assignee-marked-cell input': {
      backgroundColor: theme.palette.info.light, // A subtle light blue background
    },

    '& td': {
      whiteSpace: 'normal',
    },

    '& input': {
      width: '100%',
      boxSizing: 'border-box',
      padding: theme.spacing(0.75),
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: '4px',
      fontSize: '0.8rem',
      outline: 'none',

      '&:focus': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.disabled,
        cursor: 'not-allowed',
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5),
        fontSize: '0.7rem',
      },
    },

    '& .bold-row': {
      fontWeight: 'bold',
      backgroundColor: theme.palette.grey[100],
    },
  },
}));

const FillTaskFormPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [taskData, setTaskData] = useState(null);
  const [formTemplateData, setFormTemplateData] = useState(null);
  const [editedData, setEditedData] = useState([]);
  // CORRECTED: useState hook for loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const theme = useTheme();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const currentUserId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user'))._id : null;
  console.log("FillTaskFormPage - Current User ID:", currentUserId);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleViewImage = () => {
    if (imagePreviewUrl) {
      window.open(imagePreviewUrl, 'ImagePopUp', 'width=800,height=600,resizable=yes,scrollbars=yes');
    } else {
      showSnackbar('No image associated with this form.', 'info');
    }
  };

  useEffect(() => {
    const fetchTaskAndFormData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('authToken');
        if (!token) {
          showSnackbar("Not authenticated. Please log in.", 'error');
          setLoading(false);
          navigate('/login');
          return;
        }

        const response = await axios.get(`${baseUrl}/api/scheduled-tasks/${taskId}/get-form-for-filling`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const fetchedTask = response.data.task;
        const fetchedFormTemplate = response.data.formTemplate;

        setTaskData(fetchedTask);
        setFormTemplateData(fetchedFormTemplate);

        let initialEditedData;
        if (fetchedTask.filledFormData && fetchedTask.filledFormData.length > 0) {
          initialEditedData = fetchedTask.filledFormData.map(row => ({
            ...row,
            cells: row.cells.map(cell => {
              if (!cell) return null;

              // Ensure cell.value is a string for processing
              const cellValueAsString = String(cell.value || '');
              
              // Determine if this cell was originally marked by an assignee
              const wasMarkedByAssignee = cellValueAsString.startsWith(ASSIGNEE_MARKER);

              // Store the cleaned value for display and originalValue comparison
              const cleanedValue = wasMarkedByAssignee ? cellValueAsString.replace(ASSIGNEE_MARKER, '') : cellValueAsString;

              return {
                ...cell,
                value: cleanedValue, // Display the CLEANED value
                originalValue: cleanedValue, // Original value (cleaned) for comparison
                isAssigneeMarked: wasMarkedByAssignee, // Flag for styling and reviewer edit logic
              };
            })
          }));
        } else {
          // If no filled data, initialize from template
          initialEditedData = fetchedFormTemplate.data.map(row => ({
            ...row,
            cells: row.cells.map(cell => (cell ? {
              ...cell,
              value: String(cell.value || ''), // Ensure template values are strings
              originalValue: '', // No original value for a new form
              isAssigneeMarked: false, // Not marked initially
            } : null))
          }));
        }
        setEditedData(initialEditedData);

        if (fetchedFormTemplate && fetchedFormTemplate.associatedImage) {
          const fullImageUrl = `${baseUrl}${fetchedFormTemplate.associatedImage}`;
          setImagePreviewUrl(fullImageUrl);
          console.log('Frontend: Attempting to load image from:', fullImageUrl);
        } else {
          setImagePreviewUrl('');
          console.log('Frontend: No associated image found or path is empty.');
        }

        console.log('FillTaskFormPage - Fetched Task Data:', fetchedTask);
        console.log('FillTaskFormPage - Fetched Form Template Data:', fetchedFormTemplate);

      } catch (err) {
        console.error('FillTaskFormPage - Error fetching task/form data:', err);
        if (err.response && err.response.status === 404) {
          setError('Task or Form not found. It might have been deleted or the ID is incorrect.');
          showSnackbar('Task or Form not found.', 'error');
        } else if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          setError('Authentication failed. Please log in.');
          showSnackbar('Authentication failed. Please log in.', 'error');
          navigate('/login');
        } else {
          setError('Failed to load task/form data. Please check your network and try again.');
          showSnackbar('Failed to load form data.', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTaskAndFormData();
    }
  }, [taskId, navigate, currentUserId]);


  const isFormEditable = () => {
    if (!taskData || !currentUserId) return false;

    const isTaskCompleted =
      taskData.approvalStatus === 'Approved' ||
      taskData.approvalStatus === 'Rejected' ||
      taskData.reviewStatus === 'Rejected' ||
      taskData.assignmentStatus === 'Rejected';

    if (isTaskCompleted) {
      console.log("isFormEditable -> Task is completed, not editable.");
      return false;
    }

    const isAssigneeTurn = taskData.assignedToId === currentUserId && taskData.assignmentStatus === 'Pending';
    const isReviewerTurn = taskData.reviewerId === currentUserId &&
      taskData.reviewStatus === 'Pending' &&
      taskData.assignmentStatus === 'Submitted';
    const isApproverTurn = taskData.approverId === currentUserId &&
      taskData.approvalStatus === 'Pending' &&
      taskData.reviewStatus === 'Approved';

    const canEdit = isAssigneeTurn || isReviewerTurn || isApproverTurn;
    console.log("isFormEditable ->",
      "isAssigneeTurn:", isAssigneeTurn,
      "isReviewerTurn:", isReviewerTurn,
      "isApproverTurn:", isApproverTurn,
      "Can Edit (overall):", canEdit);
    return canEdit;
  };


  const currentIsAssigneeTurn = taskData && taskData.assignedToId === currentUserId && taskData.assignmentStatus === 'Pending';
  const currentIsReviewerTurn = taskData && taskData.reviewerId === currentUserId && taskData.reviewStatus === 'Pending' && taskData.assignmentStatus === 'Submitted';
  const currentIsApproverTurn = taskData && taskData.approverId === currentUserId && taskData.approvalStatus === 'Pending' && taskData.reviewStatus === 'Approved';


  const handleSubmitForm = async (actionType) => {
    console.log('Submitting form for task:', taskId, 'with action:', actionType, 'comment:', comment);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showSnackbar("Not authenticated. Please log in.", 'error');
        navigate('/login');
        return;
      }

      const dataToSend = editedData.map(row => ({
        ...row,
        cells: row.cells.map(cell => {
          if (!cell) return null;

          let valueToProcess = String(cell.value || ''); // Use the currently displayed (cleaned) value

          // Check if the current user is the assignee and they are submitting.
          // This ensures the marker is only added by the assignee on submission.
          const isAssigneeSubmittingThisForm = currentIsAssigneeTurn && actionType === 'submit';

          // Determine if the value has changed from its original (cleaned) state or if it's a new, non-empty value
          const hasChangedFromOriginal = cell.originalValue !== valueToProcess;
          const isNewlyFilled = cell.originalValue === '' && valueToProcess !== '';

          // ONLY add the marker if:
          // 1. It's the assignee submitting.
          // 2. The value has changed from its *original cleaned* state OR it's a new non-empty value.
          // 3. The value doesn't already start with the marker (to prevent double marking if assignee re-edits before review)
          if (isAssigneeSubmittingThisForm && (hasChangedFromOriginal || isNewlyFilled) && valueToProcess && !valueToProcess.startsWith(ASSIGNEE_MARKER)) {
            valueToProcess = ASSIGNEE_MARKER + valueToProcess;
          }
          // If it's a reviewer/approver, or assignee is not changing anything, send value as is (cleaned).
          // If a reviewer edits an assignee-marked cell, the marker will not be re-added here.

          // Ensure originalValue and isAssigneeMarked are NOT sent to backend in filledFormData
          return {
            ...cell,
            value: valueToProcess, // This is the value that will be sent (might be marked or clean)
            originalValue: undefined, // Remove originalValue from payload
            isAssigneeMarked: undefined, // Remove this flag from payload
          };
        })
      }));

      const payload = {
        filledFormData: dataToSend,
        action: actionType,
        comment: comment,
      };

      await axios.put(`${baseUrl}/api/scheduled-tasks/${taskId}/submit-form-task`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      showSnackbar(`Form ${actionType === 'submit' ? 'submitted' : actionType} successfully!`, 'success');
      navigate('/dashboard/my-task'); // Navigate after successful submission
    } catch (err) {
      console.error('FillTaskFormPage - Error submitting form data:', err);
      const errorMessage = err.response?.data?.message || `Failed to ${actionType} form. Please try again.`;
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCellChange = (rowIndex, cellIndex, value) => {
    setEditedData(prevEditedData => {
      const updatedData = [...prevEditedData];
      if (updatedData[rowIndex] && updatedData[rowIndex].cells[cellIndex]) {
        updatedData[rowIndex].cells[cellIndex] = {
          ...updatedData[rowIndex].cells[cellIndex],
          value: value, // Update the displayed value directly
          // We intentionally don't change `isAssigneeMarked` here.
          // That flag indicates original source, not current state of edit.
          // It will get re-evaluated and set on next `useEffect` fetch.
        };
      }
      return updatedData;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_DESKTOP})` }}>
        <CircularProgress />
        <Typography ml={2}>Loading form...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_DESKTOP})` }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/dashboard/my-task')}>
          Go Back to Tasks
        </Button>
      </Box>
    );
  }

  if (!taskData || !formTemplateData || editedData.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_DESKTOP})` }}>
        <Typography variant="body1">No task or form data available for filling.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/dashboard/my-task')}>
          Go Back to Tasks
        </Button>
      </Box>
    );
  }

  const formContentEditable = isFormEditable();

  return (
    <PageContainer>
      <FormPaper>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            mt: { xs: 8, sm: 9, md: 0 },
            textAlign: 'center',
            color: theme.palette.text.primary,
            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' },
            lineHeight: 1.2,
            wordBreak: 'break-word',
            paddingX: { xs: 1, sm: 0 }
          }}
        >
          Fill Form: {taskData.formName || formTemplateData.formName || 'Untitled Form'}
        </Typography>

        {imagePreviewUrl && (
          <Card sx={{ maxWidth: '80%', margin: '20px auto', boxShadow: theme.shadows[2], borderRadius: '8px' }}>
            <CardContent sx={{ textAlign: 'center', pb: 0 }}>
              <Typography variant="subtitle1" component="div" sx={{ mb: 1, fontWeight: 'medium' }}>
                Associated Image for Reference
              </Typography>
            </CardContent>
            <CardMedia
              component="img"
              sx={{
                maxHeight: { xs: 150, sm: 200, md: 300 },
                objectFit: 'contain',
                width: '100%',
              }}
              image={imagePreviewUrl}
              alt="Associated Form Image"
            />
            <CardContent sx={{ textAlign: 'center', pt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleViewImage}
                sx={{ mt: 1, textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                View Full Size Image
              </Button>
            </CardContent>
          </Card>
        )}

        <TableWrapper>
          <table>
            <thead>
              {editedData[0] && (
                <tr className={editedData[0].isRowBold ? 'bold-row' : ''}>
                  {editedData[0].cells.map((cell, cellIndex) => {
                    if (cell === null || (cell.mergeRange && !cell.isMergedTopLeft)) {
                      return null;
                    }
                    // Apply fillColor directly here for header cells
                    const cellStyle = {
                      fontWeight: cell.isCellBold ? 'bold' : 'normal',
                      // Apply backend fillColor or default if not specified
                      backgroundColor: (cell.fillColor && cell.fillColor === "#FF00") ? '#66e0f2' : cell.fillColor,
                      color: cell.fontColor || 'inherit',
                    };
                    return (
                      <th
                        key={`header-cell-${cellIndex}`}
                        colSpan={cell.colspan || 1}
                        rowSpan={cell.rowspan || 1}
                        style={cellStyle}
                      >
                        {cell.value}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            <tbody>
              {editedData.slice(1).map((rowObj, rowIndex) => {
                return (
                  <tr key={`data-row-${rowIndex}`} className={rowObj.isRowBold ? 'bold-row' : ''}>
                    {rowObj.cells.map((cell, cellIndex) => {
                      if (!cell || (cell.mergeRange && !cell.isMergedTopLeft)) {
                        return null;
                      }

                      // Apply fillColor directly here for data cells
                      const cellStyle = {
                        fontWeight: cell.isCellBold ? 'bold' : 'normal',
                        backgroundColor: (cell.fillColor && cell.fillColor === "#FF00") ? '#66e0f2' : cell.fillColor,
                        color: cell.fontColor || 'inherit',
                      };

                      // Check if the cell has any original value (meaning it was previously filled)
                      const hasOriginalValue = cell.originalValue !== undefined &&
                                               cell.originalValue !== null &&
                                               cell.originalValue !== '';

                      let isCellActuallyEditable = false; // Default to not editable

                      if (formContentEditable) { // Overall form state allows editing
                        if (currentIsAssigneeTurn) {
                          // Assignee's turn: Can ONLY fill in cells that are currently empty (no original value)
                          isCellActuallyEditable = !hasOriginalValue;
                        } else if (currentIsReviewerTurn) {
                          // Reviewer's turn: Can ONLY edit cells that were explicitly marked as assignee-filled
                          // This leverages the `isAssigneeMarked` flag set during data fetch.
                          isCellActuallyEditable = cell.isAssigneeMarked;
                        }
                        // Approver's turn: By default, cells are not editable for approver in this context.
                        // They interact via the approve/reject buttons.
                      }

                      return (
                        <td
                          key={`data-cell-${rowIndex}-${cellIndex}`}
                          colSpan={cell.colspan || 1}
                          rowSpan={cell.rowspan || 1}
                          style={cellStyle}
                          // Apply the class if the `isAssigneeMarked` flag is true for this cell
                          className={cell.isAssigneeMarked ? 'assignee-marked-cell' : ''}
                        >
                           <input
                              type="text"
                              value={cell.value || ''} // Always show the clean value
                              disabled={!isCellActuallyEditable} // Use the new specific editability flag
                              onChange={(e) => handleCellChange(rowIndex + 1, cellIndex, e.target.value)}
                            />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrapper>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: theme.spacing(2), mt: 4, flexDirection: 'column', alignItems: 'center' }}>
          {formContentEditable && (
            <>
                {currentIsAssigneeTurn && (
                    <Button variant="contained" color="primary" onClick={() => handleSubmitForm('submit')}
                      sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: '300px' }}
                    >
                        Submit Form (Assignee)
                    </Button>
                )}

                {(currentIsReviewerTurn || currentIsApproverTurn) && (
                    <TextField
                        label={currentIsReviewerTurn ? "Review Comment (Optional)" : "Approval Comment (Optional)"}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={2}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        sx={{ mb: 2, mt: 2, maxWidth: '500px', width: { xs: '100%', sm: 'auto' } }}
                    />
                )}

                {currentIsReviewerTurn && (
                    <Box sx={{ display: 'flex', gap: theme.spacing(2), width: { xs: '100%', sm: 'auto' }, justifyContent: 'center' }}>
                        <Button variant="contained" color="success" onClick={() => handleSubmitForm('approve')}
                          sx={{ flexGrow: 1 }}
                        >
                            Review Submit
                        </Button>
                        {/* Optional: Reviewer reject button */}
                        {/* <Button variant="contained" color="error" onClick={() => handleSubmitForm('reject')}
                          sx={{ flexGrow: 1 }}
                        >
                            Reject Review
                        </Button> */}
                    </Box>
                )}

                {currentIsApproverTurn && (
                    <Box sx={{ display: 'flex', gap: theme.spacing(2), width: { xs: '100%', sm: 'auto' }, justifyContent: 'center' }}>
                        <Button variant="contained" color="success" onClick={() => handleSubmitForm('approve')}
                          sx={{ flexGrow: 1 }}
                        >
                            Approve Task
                        </Button>
                        {/* <Button variant="contained" color="error" onClick={() => handleSubmitForm('reject')}
                          sx={{ flexGrow: 1 }}
                        >
                            Reject Task
                        </Button> */}
                    </Box>
                )}
            </>
          )}

          <Button variant="outlined" onClick={() => navigate('/dashboard/my-task')}
            sx={{ mt: (formContentEditable) ? 2 : 0, width: { xs: '100%', sm: 'auto' }, maxWidth: '300px' }}
          >
            Back to My Tasks
          </Button>
        </Box>
      </FormPaper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default FillTaskFormPage;